import API from './API';
import {ContactsResponse, Owner, InvestmentGoal, V, RecordsEntity} from '../Agent/types';
import RootStore from '../Core/mobx/RootStore';
import {CompleteUserData, UserIdData} from '@amplify/athena-models';

type ConsentSuccess = {
    consent: {
        ip: string;
        date: number;
        consentAuthUrl?: string;
    };
};

type ConsentError = {
    consent: {
        status: false;
        message: string;
    };
};

export type Phase3Type = {
    selectedTab: string;
    temporary: {
        yearCoverage: number;
        coveragePeriod: number;
        defaultCoveragePeriod: number;
        coveragePeriodRange: number[];
        illness: number[];
    };
    basicPermanent: {
        lifetimeCoverage: number;
        lifetimeCoverageRange: number[];
        illness: string[];
    };
    bestValue: {
        yearCoverage: number;
        lifetimeCoverage: number;
        illness: string[];
    };
    retirementFocused: {
        lifetimeCoverage: number;
        illness: string[];
    };
    resultTemporary: {
        perMonth?: string;
        lifetimeCoverage: string | null;
        yearCoverage: string;
        riders: string[];
        carriers: string[];
    };
    resultBasic: {
        perMonth: string;
        lifetimeCoverage: string;
        riders: string[];
        carriers: string[];
    };
    resultBest: {
        perMonth: number | null;
        year1_20Coverage: number | null;
        year20_LifeCoverage: number | null;
        taxFreeCash: number | null;
        years: number | null;
        riders: string[];
        carriers: string[];
    };
    resultRetirement: {
        perMonth: number | null;
        lifetimeCoverage: number | null;
        taxFreeCash: number | null;
        years: number | null;
        riders: string[];
        carriers: string[];
    };
    vul: {
        deathBenefit: number | null;
        contribution: number | null;
        total_contribution: number | null;
        vul: number | null;
        spx: number | null;
    };
};

export type ClientWalkthroughStateUserData = {
    userData: {
        phase1: {
            investmentRisk: 'high' | 'moderate' | 'low';
            usCitizen: boolean;
            birthCountry: string;
            birthState: string;
            zipCode: string;
            zipCodeId: number;
            zipCodeState: string;
            address: {
                Street?: string;
                City?: string;
                State?: string;
                ZipCode?: string;
                Country?: string;
            };
            employmentStatus: string;
            features: InvestmentGoal[];
            maritalStatus: string;
            numberOfChildren: string;
            military: boolean;
            annualIndividualIncome: string;
            annualHouseholdIncome: string;
            annualHouseholdIncomeText: string;
            estimatedSavingTotal: string;
            estimatedDebtTotal: number;
            travellingOut: string;
            extremeSports: string[];
            existingCoverage: string;
            unemploymentReasonDesc: string;
            unemploymentReasonType: string;
            prevEmployer: string;
            prevOccupation: string;
            workExpirienceYears: string;
            currIncomeDetails: string;
            ageOfRetirement: string;
            schoolName: string;
            grade: string;
            termSSDI: string;
            coverageAmount: string | number;
            policyType: string;
            policyTypeApplied: string;
            coverageAmountApplied: string;
            existingCoverageApplied: string;
        };
        phase2: {
            healthFamilyMedical: string[];
            primaryCare: string;
            aids: boolean;
            alcoholicDrinksWeekly: string;
            diagCond: {
                value: boolean;
                label: string;
            }[];
            behaviors: {
                value: boolean;
                label: string;
            }[];
            alcoType: string;
            alcoBeverages: boolean;
            treatment: boolean;
            treatmentClinic: string;
            alcoDrugFreeYears: number;
            treatmentDetails: string;
            treatmentWhen: string;
            DUIWhen: string;
            suspendedLicenseStill: boolean;
            suspendedLicenseWhen: string;
            movingViolationsHowMany: string;
            criminalOffenseWhen: string;
            criminalOffenseDetails: string;
            paroleComplete: string;
            bankruptcyYear: string;
            bankruptcyChapter: number;
            bankruptcyIsOpen: boolean;
            bankruptcyDischargedWhen: string;
            diagBloodPressDiagnosisDate: string;
            diagBloodPressHigh: string;
            diagBloodPressLow: string;
            diagBloodPressMedication: string;
            diagHighCholHDL: number;
            diagDiabetesA1C: number;
            diagCancerWhen: string;
            diagBasalCellCancer: string;
            usedTobacco: boolean;
            usedTobaccoProduct: string;
            usedTobaccoFrequency: string;
            usedTobaccoLastDate: string;
            usedMarijuana: boolean;
            usedMarijuanaProduct: string;
            usedMarijuanaFrequency: string;
            usedMarijuanaLastDate: string;
            otherSmokeProductUsed: boolean;
            otherSmokeProductSubstance: string;
            otherSmokeProductProduct: string;
            otherSmokeProductFrequency: string;
            otherSmokeProductLastDate: string;
            diagProductsUsed: string[];
            diagDiabetesType: boolean;
            diagPsychMedication: number;
            diagPsychLastHosp: boolean;
            diagPsychCondition: string[];
            diagSleepLevel: string;
            diagSleepCPAP: boolean;
        };
        phase3: Phase3Type;
    };
};

export type ClientWalkthroughStateResponse = {
    p: string;
    s: string;
    v: {
        calcStore: {
            recommendedProductType: string;
            hasSelectedProduct: boolean;
            activeProduct: 'iul' | 'vul' | 'combo' | 'term';
        };
    } & ClientWalkthroughStateUserData;
};

export type ClientWalkthroughDataResponse = {
    p: string;
    s: string;
    v?: ClientWalkthroughData;
};

export type ClientWalkthroughState = {
    userData: ClientWalkthroughStateUserData;
};

export type ClientWalkthroughData = {
    feet: number;
    inch: number;
    date_of_birth: string;
    weight: number;
    condition: {[key: string]: any};
    gender: number;
    first_name: string;
    last_name: string;
    email: string;
    best: any;
    retirement: any;
    temporary: any;
    basic: any;
    driving: any;
    high_blood_pressure: any;
    smoking: any;
    monthly_budget: any;
    call_date: any;
    call_time_slot: any;
    approval: any;
    uuid: string;
    phone_number: string;
    household_income: number;
    updated_at: string;
    userData: CompleteUserData;
    userIdData: UserIdData;
};

export type ClientQuoteProperties = {
    property: string;
    value?: string;
};
export type ClientQuoteData = {
    email: string;
    timestamp: number;
    v: {
        properties: ClientQuoteProperties[];
    };
};

export default class CRM {
    public static async GetContacts(search = '', limit = 10, after = '0'): Promise<ContactsResponse | null> {
        const options = {
            sorts: [
                {
                    propertyName: 'createdate',
                    direction: 'DESCENDING',
                },
            ],
            limit,
            after,
            properties: [
                'firstname',
                'lastname',
                'email',
                'company',
                'phone',
                'address',
                'city',
                'state',
                'zip',
                'hs_lead_status',
                'hubspot_owner_id',
                'createdate',
            ],
        } as any;

        if (search != '') {
            options['query'] = search;
        }

        const ret = await API.Call<ContactsResponse>('crm/contacts/search', 'POST', options);
        return ret;
    }

    public static async CreateContact(firstname: string, lastname: string, phone: string, email: string): Promise<any> {
        var ret = await API.Call('crm/contacts', 'POST', {
            firstname,
            lastname,
            phone,
            email,
        });
        return ret;
    }

    private static OwnerIDsInProgress = new Map<string, Promise<any>>();

    public static async OwnerInfoFromID(id: string): Promise<Owner | undefined> {
        if (id == null || id == '') {
            return undefined;
        }

        const owner = localStorage.getItem('owner-' + id);
        if (owner != null) {
            return JSON.parse(owner);
        }

        const promise = CRM.OwnerIDsInProgress.get(id);
        if (promise != null) {
            return promise;
        }

        const pr = new Promise<any>((resolve, reject) => {
            API.Call('owner/' + id, 'GET').then((resp) => {
                localStorage.setItem('owner-' + id, JSON.stringify(resp));
                CRM.OwnerIDsInProgress.delete(id);
                resolve(resp);
            });
        });

        CRM.OwnerIDsInProgress.set(id, pr);
        return pr;
    }

    public static async GetClientRecords(email: string) {
        const ret = await API.Call<RecordsEntity[]>(`client/${email}`, 'GET');
        return ret;
    }

    public static async GetClientRecord(email: string, recordid: string) {
        const ret = await API.Call<V>(`client/${email}/${recordid}`, 'GET');
        return ret;
    }

    public static async SetClientRecord(email: string, recordid: string, data: any) {
        const ret = await API.Call('client/' + email + '/' + recordid, 'POST', data);
        return ret;
    }

    public static async GetCustomerConsent(firstName: string, lastName: string, dob: string | number) {
        const firstNameEncoded = encodeURIComponent(firstName);
        const lastNameEncoded = encodeURIComponent(lastName);

        const ret = await API.Call<ConsentSuccess | ConsentError>(
            `customer/${firstNameEncoded}/${lastNameEncoded}/${dob}/consent`,
            'GET',
        );
        return ret;
    }

    public static async GetCustomerConsentCode(firstName: string, lastName: string, dob: number) {
        const firstNameEncoded = encodeURIComponent(firstName);
        const lastNameEncoded = encodeURIComponent(lastName);

        const ret = await API.Call<{code: string}>(
            `customer/${firstNameEncoded}/${lastNameEncoded}/${dob}/consent-code`,
            'POST',
            {},
        );
        return ret;
    }

    public static async GetWalkthroughClientData(email: string) {
        const ret = await API.Call<ClientWalkthroughDataResponse[]>(`customer/${email}/walkthrough`, 'GET', {});
        return ret;
    }

    public static async GetWalkthroughClientState(uuid: string) {
        const ret = await API.Call<ClientWalkthroughStateResponse[]>(`customer/${uuid}/walkthrough-state`, 'GET', {});
        return ret;
    }

    public static async getLatestCRMWalkthroughStateUserData(
        userId: string,
    ): Promise<ClientWalkthroughStateResponse['v']> {
        const resp = await CRM.GetWalkthroughClientState(userId);

        if (!resp) {
            throw new Error('Failed to get walkthrough client data');
        }

        const sorted = resp.sort((a, b) => new Date(b.s).getTime() - new Date(a.s).getTime());
        const [data] = sorted;
        return data?.v;
    }

    public static async GetCustomerVerifyDetailCode(formId: string, email: string) {
        const ret = await API.Call<{code: string}>(`verify/verify-code`, 'POST', {formId, email});
        return ret;
    }

    public static async getCRMWalkthroughData(
        email: string,
    ): Promise<{data: ClientWalkthroughData; walkthroughState: ClientWalkthroughStateResponse['v']}> {
        const resp = await CRM.GetWalkthroughClientData(email);

        if (!resp) {
            throw new Error('Failed to get walkthrough client data');
        }

        const [data] = resp.sort(
            (a, b) => new Date(b.v?.updated_at || '').getTime() - new Date(a.v?.updated_at || '').getTime(),
        );

        if (!data?.v) {
            RootStore.uiStore.showSnackbar('Failed to fetch walkthrough data', 'error');
            throw new Error(
                'Failed to fetch walkthrough data. You might not be using the correct athena url, please double check your athena url.',
            );
        }

        return {
            data: data.v as any,
            walkthroughState: await CRM.getLatestCRMWalkthroughStateUserData(data.v.uuid),
        };
    }

    public static async GetClientHistoryRecords(email: string) {
        const ret = await API.Call<{forms: {s: string}[]}>(`client/${email}/history`, 'GET');
        return ret;
    }

    public static async GetClientHistoryRecord(email: string, sortKey: string) {
        const ret = await API.Call<V>(`client/form?email=${email}&sortKey=${sortKey}`, 'GET');
        return ret;
    }
}
